import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { useGroupStore } from '../../stores/group';
import {
  getApprovalStats,
  getPendingApprovals,
  ApprovalInboxTabView,
  ApprovalStats,
  PendingApprovalItem,
  getDocStatusBadgeTone,
  getReportTypeLabel,
  getReportTypeBadgeTone,
} from '../../lib/reports';
import { resolveReportStatusLabel } from '../../lib/reportStatusLabel';
import { sitesApi, Site } from '../../lib/organizations';
import { getEvidenceDisplayName, ActorNameInput } from '@safetyhub/shared';
import { Badge, Card, PAGER_BUTTON_CLASS } from '@safetyhub/ui';
import {
  CalendarIcon,
  CheckSquareIcon,
  ChevronDownIcon,
  ClipboardCheckIcon,
  ClockIcon,
  DocumentIcon,
  HourglassIcon,
  MapPinIcon,
} from '../../components/icons';
import { getReportTypeTileClass } from '../../components/reports/reportTypeDisplay';
import { StatTile } from '../../components/StatTile';
import { ApprovalFlowStepper } from './ApprovalFlowStepper';
import { buildApprovalFlowNodes, formatWaitDuration, resolveSubmittedAt } from './approvalFlow';
import { APPROVALS_PATH, APPROVALS_LABEL } from './approvalsNav';

// ── 데스크톱 리스킨 공통 클래스 (목업 결재함 페이지 톤 — P3-1 문서관리 레퍼런스) ──

/**
 * 필터 셀렉트 — 목업 결재함 전용 `.ff-select` + `.ff-value`
 * (`.ff-value{font-size:15.5px; font-weight:700; color:ink-900; letter-spacing:-.01em}`).
 *
 * **박스가 없는 게 이 컨트롤의 정체다** — 목업 `.ff-row`에서 테두리는 행 카드 하나뿐이고,
 * 필드는 라벨 위·굵은 값 아래의 맨 텍스트 + 우측 쉐브론이다(hover 시 값 brand-700).
 * 그래서 native select의 외형을 지우고(appearance-none·투명 배경·무테두리) 쉐브론은
 * 감싸는 래퍼가 따로 얹는다. focus 링만은 목업에 없어도 남긴다(borderless라 키보드
 * 사용자가 위치를 잃는다 — focus-visible로 마우스에는 안 보이게).
 *
 * ⚠️ **`@safetyhub/ui`의 `FILTER_SELECT_CLASS`와 다른 컴포넌트다** — 그쪽은 목업 9종 공통
 * `.filter-dd`(박스형, 14.5px/600/ink-700)이고, 이건 결재함에만 있는 `.ff-row` 안의 큰
 * 필드형이다. 값이 갈린 게 아니라 애초에 다른 것이라 공용으로 올리지 않고 **이름을 갈라**
 * 여기 남긴다. 통합하려 들지 말 것.
 *
 * `w-full`을 상수에 넣은 것도 그 때문 — `.ff-field`가 행을 채우는 구조라 폭이 이 컨트롤의
 * 성질이다(`.filter-dd` 쪽은 소비처가 폭을 정하므로 상수에 폭이 없다).
 */
const FIELD_VALUE_SELECT_CLASS =
  'w-full cursor-pointer appearance-none rounded-[6px] bg-transparent pr-8 text-[15.5px] font-bold tracking-[-0.01em] text-ink-900 transition-colors group-hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';

/**
 * 작성자 표시명 — 탈퇴 사용자 처리를 포함해 스냅샷 우선순위대로 고른다
 * (authorSignerName → authorSnapshotName → 현재 member 이름).
 */
function resolveAuthorDisplayName(report: PendingApprovalItem['report']): string {
  return getEvidenceDisplayName(
    report.authorSignerName ?? report.authorSnapshotName ?? null,
    report.author
      ? ({
          name: report.author.member?.name ?? report.author.name,
          deletedAt: report.author.deletedAt,
        } as ActorNameInput)
      : null,
  );
}

/** 메타행 날짜 — 목업 `.appr-sub`의 `2026.05.22` 표기. */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;
}

/**
 * 탭 = 목록 범위의 정본. 배열 순서가 곧 탭 순서이고, `view`가 `tabCounts`의 키와 같아
 * 배지를 따로 매핑하지 않는다.
 */
const INBOX_TABS: { view: ApprovalInboxTabView; label: string }[] = [
  { view: 'pending', label: '결재 대기' },
  { view: 'submitted', label: '내가 제출' },
  { view: 'processed', label: '처리 완료' },
  { view: 'rejected', label: '반려' },
];

/**
 * '내 결재만 보기'가 뜻을 갖는 범위 — 조직 전체가 기본인 탭에서만 좁힐 게 있다.
 * submitted는 서버가 이미 "내가 작성" 기준으로 좁힌 목록이라, 두면 눌러도 아무 일이
 * 없는 죽은 컨트롤이 된다.
 */
function supportsMyOnly(view: ApprovalInboxTabView): boolean {
  return view !== 'submitted';
}

/** 목록이 비었을 때의 문구 — 범위마다 "없다"의 뜻이 달라 탭별로 갈라 쓴다. */
const EMPTY_COPY: Record<ApprovalInboxTabView, { title: string; description: string }> = {
  pending: {
    title: '결재 대기 건이 없습니다',
    description: '현재 결재가 필요한 보고서가 없습니다.',
  },
  submitted: {
    title: '결재 진행 중인 제출 문서가 없습니다',
    description: '내가 제출한 보고서 중 결재가 진행 중인 건이 없습니다.',
  },
  processed: {
    title: '처리 완료된 결재가 없습니다',
    description: '승인이 끝난 보고서가 아직 없습니다.',
  },
  rejected: {
    title: '반려된 보고서가 없습니다',
    description: '반려 처리된 보고서가 없습니다.',
  },
};

/**
 * 평균 결재 소요 — 서버가 소수 첫째 자리까지 내려주고, 대상 0건이면 null이다.
 *
 * 값과 단위를 갈라 내는 건 카드가 그렇게 짜여 있기 때문이다 — 목업 `.stat-v`는 큰 숫자
 * (`.num`)와 작은 단위(`.unit`)를 다른 크기·굵기·색으로 세우므로 `'2.4시간'` 한 덩이로는
 * 그 결을 낼 수 없다. 대상이 없을 때는 붙일 단위 자체가 없어 `unit`을 비운다.
 */
function formatAvgDuration(hours: number | null): { value: string; unit?: string } {
  if (hours === null) return { value: '—' };
  return { value: Number.isInteger(hours) ? String(hours) : hours.toFixed(1), unit: '시간' };
}

export default function ApprovalsPage() {
  const { currentMembership } = useAuthStore();
  const currentGroupId = useGroupStore((s) => s.currentGroupId);
  const companyId = currentMembership?.companyId;

  const [approvals, setApprovals] = useState<PendingApprovalItem[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [stats, setStats] = useState<ApprovalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 필터 (탭 = 목록 범위)
  const [view, setView] = useState<ApprovalInboxTabView>('pending');
  const [siteFilter, setSiteFilter] = useState<string>('');
  const [myOnlyFilter, setMyOnlyFilter] = useState(false);

  // 페이지네이션
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 10;

  // 그룹 전환 시 현장 필터를 비운다 — 이전 그룹의 siteId가 남으면 group∩site 교집합으로
  // 0건이 되고 드롭다운도 선택값을 못 그린다. 최초 마운트는 건너뛰고 실제 전환만 초기화한다.
  const prevGroupIdRef = useRef(currentGroupId);
  useEffect(() => {
    if (prevGroupIdRef.current === currentGroupId) return;
    prevGroupIdRef.current = currentGroupId;
    setSiteFilter('');
    setPage(1);
  }, [currentGroupId]);

  useEffect(() => {
    if (companyId) {
      loadSites();
    }
  }, [companyId, currentGroupId]);

  useEffect(() => {
    if (companyId) {
      loadApprovals();
    }
  }, [companyId, siteFilter, myOnlyFilter, view, page, currentGroupId]);

  const loadSites = async () => {
    if (!companyId) return;
    try {
      const data = await sitesApi.findAll(companyId, currentGroupId);
      setSites(data);
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  };

  const loadStats = async () => {
    if (!companyId) return;
    try {
      setStats(
        await getApprovalStats(companyId, {
          siteId: siteFilter || undefined,
          groupId: currentGroupId,
        }),
      );
    } catch (err) {
      // 통계는 보조 정보다 — 실패해도 목록은 그대로 쓰게 두고 값만 비운다.
      setStats(null);
      console.error('Failed to load approval stats:', err);
    }
  };

  const loadApprovals = async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getPendingApprovals(companyId, {
        siteId: siteFilter || undefined,
        groupId: currentGroupId,
        myOnly: supportsMyOnly(view) ? myOnlyFilter : false,
        limit,
        offset: (page - 1) * limit,
        view,
      });
      setApprovals(result.data);
      setTotal(result.meta.total);
      // 결재를 처리하고 돌아오면 대기/오늘 처리 수가 달라진다. 목록을 다시 읽는 지점이
      // 곧 그 복귀 지점이라 통계도 같이 맞춘다.
      await loadStats();
    } catch (err) {
      setError('결재 목록을 불러오는데 실패했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 탭 전환. `myOnly`를 넘기는 건 카드 바로가기뿐이다 — 카드가 보여 준 값이 "내 기준"이라
   * 목록도 같은 축으로 열어야 값과 목록이 맞는다. 탭을 직접 누를 때는 체크 상태를 그대로 둔다.
   */
  const selectView = (next: ApprovalInboxTabView, options?: { myOnly?: boolean }) => {
    setView(next);
    setPage(1);
    if (options?.myOnly !== undefined) setMyOnlyFilter(options.myOnly);
  };

  const totalPages = Math.ceil(total / limit);
  const emptyCopy = EMPTY_COPY[view];

  if (!companyId) {
    return (
      <div className="text-center py-12">
        <p className="text-ink-500">조직을 선택해주세요.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">결재함</h1>
          <p className="mt-1 text-[15px] font-medium text-ink-500">
            결재 대기 중인 보고서를 확인하고 처리하세요
          </p>
        </div>
      </div>

      {/* 통계 카드 4장 — 앞 3장은 대응 탭 바로가기. 모바일은 2×2. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="내 결재 대기"
          value={stats ? String(stats.myPending) : '—'}
          unit={stats ? '건' : undefined}
          // 대기 수는 "지금 쌓여 있는 양"이라 어제와 비교하려면 어제 시점의 스냅샷이 있어야
          // 한다(처리 건수처럼 지나간 행위를 되짚어 셀 수 없다). 그 저장이 생기기 전까지
          // 이 카드에는 증감 pill을 달지 않는다.
          icon={ClockIcon}
          accentClass="text-amber-600"
          iconBgClass="bg-amber-50"
          // 카드는 "내 결재 대기"인데 탭은 조직 전체 '결재 대기'다 — '내 결재만'이 꺼진
          // 목록에서 이 카드가 선택으로 빛나면 카드 값과 다른 목록을 카드가 가리키는 셈이 된다.
          selected={view === 'pending' && myOnlyFilter}
          // 카드 값 myPending은 "내가 결재자인 건"이라, 목록도 '내 결재만'으로 열어야 값과 맞는다.
          onSelect={() => selectView('pending', { myOnly: true })}
        />
        <StatTile
          label="내가 제출"
          value={stats ? String(stats.mySubmitted) : '—'}
          unit={stats ? '건' : undefined}
          foot="결재 진행 중인 문서"
          icon={DocumentIcon}
          accentClass="text-ink-500"
          iconBgClass="bg-ink-100"
          selected={view === 'submitted'}
          onSelect={() => selectView('submitted')}
        />
        <StatTile
          label="오늘 처리 완료"
          value={stats ? String(stats.processedToday) : '—'}
          unit={stats ? '건' : undefined}
          // 어제와 같은 수면 보여 줄 변화가 없다 — '0'을 띄우면 pill이 눈만 끌고 뜻이 없다.
          pill={
            stats && stats.processedToday - stats.processedYesterday !== 0
              ? { kind: 'diff', diff: stats.processedToday - stats.processedYesterday }
              : undefined
          }
          icon={CheckSquareIcon}
          accentClass="text-emerald-600"
          iconBgClass="bg-emerald-50"
          // 내 결재 대기 카드와 같은 이유 — '내 결재만'까지 켜져 있어야 카드의 축이다.
          selected={view === 'processed' && myOnlyFilter}
          // '내가 처리한' 축은 맞추되 기간은 다르다 — 카드는 오늘분, 목록은 처리 완료 전체다.
          // 오늘만 담는 view(processed-today)를 탭으로 두지 않기로 한 이상, 축만 맞추고
          // 기간 차이는 받아들인다.
          onSelect={() => selectView('processed', { myOnly: true })}
        />
        <StatTile
          label="평균 결재 소요"
          {...(stats ? formatAvgDuration(stats.avgDurationHours) : { value: '—' })}
          foot="최근 30일 기준"
          icon={HourglassIcon}
          accentClass="text-brand-600"
          iconBgClass="bg-brand-50"
        />
      </div>

      {/* 필터 — 목업 `.ff-row`. `Card`를 쓰지 않는 건 StatTile과 같은 이유다: 행 radius가
          14px(카드 프리셋 어디에도 없는 값)이고 hover에 테두리 색이 바뀌는데, Card가 소유한
          면·테두리 위에 className으로 얹으면 생성 순서 싸움이 된다. */}
      <div className="flex flex-wrap items-center justify-between gap-6 rounded-[14px] border border-line bg-white px-[22px] py-4 shadow-card-sm transition-colors hover:border-brand-200">
        {/* .ff-field — 라벨 위·값 아래의 borderless 클릭 영역. group은 hover 시 값·쉐브론을
            함께 brand로 물들이기 위해서다(.ff-field:hover .ff-value/.chev). */}
        <div className="group min-w-[200px] flex-1">
          <label htmlFor="site-filter" className="mb-[5px] block cursor-pointer text-[12.5px] font-semibold tracking-[.005em] text-ink-500">
            현장
          </label>
          <div className="relative">
            <select
              id="site-filter"
              value={siteFilter}
              onChange={(e) => {
                setSiteFilter(e.target.value);
                setPage(1);
              }}
              className={FIELD_VALUE_SELECT_CLASS}
            >
              <option value="">전체 현장</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-0 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-ink-400 transition-colors group-hover:text-brand-600" />
          </div>
        </div>

        {supportsMyOnly(view) && (
          <label className="flex flex-none cursor-pointer select-none items-center gap-[9px] px-1 py-1.5 text-sm font-semibold text-ink-700 transition-colors hover:text-ink-900">
            <input
              type="checkbox"
              checked={myOnlyFilter}
              onChange={(e) => {
                setMyOnlyFilter(e.target.checked);
                setPage(1);
              }}
              className="h-[18px] w-[18px] rounded-[5px] border-line accent-brand-600 focus:ring-brand-500"
            />
            <span>내 결재만 보기</span>
          </label>
        )}
      </div>

      {/* 탭 — 목록 범위의 정본. 배지는 조직 전체 기준이라 '내 결재만'을 켜도 줄지 않는다
          (그 수는 카드가 보여 준다). 통계를 못 읽었을 땐 0으로 속이지 말고 배지를 뺀다.
          좁은 화면에선 라벨을 글자 단위로 꺾지 않고(nowrap) 탭바 자체를 가로 스크롤로 흘린다. */}
      <div
        className="flex items-end gap-1 overflow-x-auto border-b border-line"
        role="tablist"
        aria-label="결재함 범위"
      >
        {INBOX_TABS.map((tab) => {
          const isActive = tab.view === view;
          return (
            <button
              key={tab.view}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => selectView(tab.view)}
              className={`-mb-px inline-flex flex-none items-center gap-[7px] whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm font-semibold transition-colors ${
                isActive
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-ink-500 hover:text-ink-800'
              }`}
            >
              {tab.label}
              {stats && (
                <Badge
                  tone={isActive ? 'brand' : 'slate'}
                  size="sm"
                  className="justify-center min-w-[22px] tabular-nums"
                >
                  {stats.tabCounts[tab.view]}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      {/* 결재 목록 — 선택된 탭의 범위 */}
      <Card radius="lg" padding="none" className="overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
            <p className="mt-2 text-sm font-medium text-ink-500">로딩 중...</p>
          </div>
        ) : error ? (
          <div className="py-12 text-center text-sm font-semibold text-rose-600">{error}</div>
        ) : approvals.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardCheckIcon className="mx-auto h-12 w-12 text-ink-300" />
            <h3 className="mt-2 text-sm font-bold text-ink-900">{emptyCopy.title}</h3>
            <p className="mt-1 text-sm font-medium text-ink-500">{emptyCopy.description}</p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-line-soft">
              {approvals.map((item) => {
                // 유형 아이콘 — 모든 문서가 Composer 양식이라 기본 문서 아이콘 하나다(문서관리와 같다 ·
                // 유형별 픽토그램 맵은 없다). 타일 색은 `reportTypeDisplay`가 정본.
                const TypeIcon = DocumentIcon;
                const authorDisplayName = resolveAuthorDisplayName(item.report);
                const nodes = buildApprovalFlowNodes(item, authorDisplayName);
                const submittedAt = resolveSubmittedAt(item);
                // '처리 완료'·'반려' 탭에는 이미 승인·반려된 보고서가 들어온다 — 대기 배지와
                // 대기시간을 그대로 붙이면 처리된 문서를 아직 기다리는 것처럼 보여준다.
                const isPending = item.report.status === 'PENDING_APPROVAL';

                return (
                  <li key={item.report.id}>
                    <Link
                      to={`/reports/${item.report.id}`}
                      // 진입점 전파 — 상세의 back-link이 결재함으로 돌아오게 한다.
                      // 없으면 상세가 type 기본값(문서관리 목록)으로 폴백해, 결재함에서 들어왔는데
                      // 뒤로가기가 /reports로 새는 버그가 된다.
                      state={{ from: APPROVALS_PATH, fromLabel: APPROVALS_LABEL }}
                      // 모바일은 2열(아이콘|내용) — 3열을 유지하면 대기시간 배지가 우측 열을
                      // 통째로 쥐어 제목이 압사한다. 배지는 내용 아래 행으로 내려보낸다.
                      className="grid grid-cols-[auto_1fr] items-start gap-x-3.5 gap-y-3 px-4 py-4 transition-colors hover:bg-surface-soft sm:grid-cols-[auto_1fr_auto] sm:gap-x-5 sm:gap-y-3.5 sm:px-5 sm:py-5"
                    >
                      {/* 유형 아이콘 타일 */}
                      <div
                        className={`flex h-[46px] w-[46px] flex-none items-center justify-center rounded-card ${
                          getReportTypeTileClass(getReportTypeBadgeTone())
                        }`}
                      >
                        <TypeIcon className="h-[21px] w-[21px]" />
                      </div>

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2.5">
                          {/* min-w-0이 없으면 flex 항목이 내용 폭 아래로 줄지 못해 truncate가
                              걸리지 않고 긴 제목이 카드를 밀어낸다. */}
                          <span className="min-w-0 truncate text-base font-bold tracking-tight text-ink-900">
                            {item.report.title}
                          </span>
                          {/* 대기 태그 — 목업 `.appr-tag`. 점은 Badge의 `dot`이 아니라 children으로
                              직접 넣는다(대기 상태를 숨쉬듯 표시하려면 점에 애니메이션이 필요한데
                              Badge의 dot에는 클래스를 붙일 수 없다). */}
                          {isPending ? (
                            <Badge tone="amber" shape="rounded" className="flex-none">
                              <span
                                aria-hidden="true"
                                className="h-1.5 w-1.5 flex-none rounded-full bg-amber-600 motion-safe:animate-breathe"
                              />
                              대기
                            </Badge>
                          ) : (
                            <Badge
                              tone={getDocStatusBadgeTone(item.report.status)}
                              shape="rounded"
                              dot
                              className="flex-none"
                            >
                              {/* 라벨은 목록·상세와 같은 정본 한 벌 — 처리 탭(조직 전체 APPROVED)에
                                  결재 정책 없이 확정된 문서가 섞이면 다른 화면과 같이 '완료'로 부른다
                                  (종전 enum 전용 `getDocStatusLabel`은 그 문서만 '결재완료'라 불렀다). */}
                              {resolveReportStatusLabel({
                                status: item.report.status,
                                approvalSignatures: item.signatures,
                              })}
                            </Badge>
                          )}
                        </div>

                        {/* 메타 행 — 유형 필 · 현장 · 작성일 · 작성자. 각 항목은 nowrap —
                            좁은 화면에서 항목 **단위**로 다음 줄로 내려가야지, 배지·현장명이
                            글자 단위로 꺾이면 세로 글자 기둥이 된다(2026-08-02 모바일 실측). */}
                        <div className="mt-1.5 flex flex-wrap items-center gap-2.5 text-[13px] font-medium text-ink-500">
                          <Badge
                            tone={getReportTypeBadgeTone()}
                            shape="rounded"
                            dot
                            className="whitespace-nowrap"
                          >
                            {/* 유형 배지 = 서버가 내려주는 양식 이름(코드값·키는 화면에 새지 않는다). */}
                            {getReportTypeLabel(item.report.templateName)}
                          </Badge>
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                            <MapPinIcon className="h-3 w-3 text-ink-400" />
                            {item.report.site.name}
                          </span>
                          <span aria-hidden="true" className="h-[3px] w-[3px] rounded-full bg-ink-300" />
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums">
                            <CalendarIcon className="h-3 w-3 text-ink-400" />
                            {formatDate(item.report.createdAt)}
                          </span>
                          <span aria-hidden="true" className="h-[3px] w-[3px] rounded-full bg-ink-300" />
                          <span className="whitespace-nowrap">
                            작성자 <span className="font-semibold text-ink-700">{authorDisplayName}</span>
                          </span>
                        </div>

                      </div>

                      {/* 대기시간 — 목업 `.appr-time`. 승인/반려/재발송은 본문 확인 후 판단할
                          액션이라 목록에 인라인으로 두지 않고(2026-08-02 사용자 기각 확정),
                          비워진 자리는 아래 스텝퍼가 전폭으로 채운다. 대기 중이 아니면 칸
                          자체를 내보내지 않는다. */}
                      {isPending && (
                        // 모바일에선 내용 아래 왼쪽(2열의 다음 행), 데스크톱에선 우측 열 상단.
                        <span className="col-start-2 inline-flex items-center justify-center gap-1.5 justify-self-start whitespace-nowrap rounded-card-sm border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-bold tracking-tight text-amber-600 sm:col-start-3 sm:justify-self-end">
                          <ClockIcon className="h-3 w-3 flex-none" />
                          {formatWaitDuration(submittedAt)}
                        </span>
                      )}

                      {/* 결재 진행 현황 — 시안이 버튼 칸에 떼 주던 폭까지 스텝퍼가 쓰는 게
                          사용자 확정 레이아웃. 모바일은 카드 전폭(아이콘 열 포함)을 준다 —
                          안 그래도 좁은 화면에서 아이콘 폭만큼 들여쓰면 스크롤만 늘어난다. */}
                      <div className="col-span-2 col-start-1 sm:col-start-2">
                        <ApprovalFlowStepper nodes={nodes} />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-line px-5 py-3.5">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className={PAGER_BUTTON_CLASS}
                  >
                    이전
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className={`ml-3 ${PAGER_BUTTON_CLASS}`}
                  >
                    다음
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <p className="text-sm font-medium text-ink-500">
                    총 <span className="font-bold tabular-nums text-ink-800">{total}</span>건 중{' '}
                    <span className="font-bold tabular-nums text-ink-800">{(page - 1) * limit + 1}</span> -{' '}
                    <span className="font-bold tabular-nums text-ink-800">{Math.min(page * limit, total)}</span>건
                  </p>
                  <nav className="flex items-center gap-1.5" aria-label="페이지">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className={PAGER_BUTTON_CLASS}
                      aria-label="이전 페이지"
                    >
                      &lt;
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        aria-current={p === page ? 'page' : undefined}
                        className={`h-8 min-w-[32px] rounded-card-sm border px-2 text-sm font-semibold tabular-nums transition-colors ${
                          p === page
                            ? 'border-brand-600 bg-brand-600 text-white'
                            : 'border-line bg-white text-ink-600 hover:bg-surface-hover hover:text-ink-900'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className={PAGER_BUTTON_CLASS}
                      aria-label="다음 페이지"
                    >
                      &gt;
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
